import { FC, type CSSProperties } from 'react';
import { V4 as C } from '@components/v4/tokens';
import { Z } from '@components/v4';
import type { Nudge } from './usePaulinaChat';

/**
 * PaulinaNudgeBubble — proactividad de Paulina en DESKTOP: speech-bubble con caret
 * anclado SOBRE la pill (Opción A), que reemplaza la antigua tarjeta-nudge flotante.
 * No abre el chat solo; ofrece aceptar (CTA del nudge) o descartar (X). Se suprime
 * cuando hay pedido entrante (lo decide el contenedor: el host no lo monta).
 *
 * Patrón visual heredado de FeatureCoachmark (caret + dismiss + role=dialog). LECCIÓN
 * (PriceCoachmark/chat14): base `opacity:1` y animar SOLO `translateY` — un keyframe
 * con opacidad dentro de un contenedor con transform puede quedar stuck en 0.
 *
 * z-index `Z.paulinaPill` (80, misma capa que la pill, debajo del toast `Z.pedido`=99).
 * Animación NUEVA respetuosa de `prefers-reduced-motion`: el deslizamiento de entrada
 * solo corre bajo `@media (prefers-reduced-motion: no-preference)`; con motion reducido
 * la burbuja aparece sin animarse.
 */

const KF_ID = 'plick-paulina-nudge-kf';
if (typeof document !== 'undefined' && !document.getElementById(KF_ID)) {
  const st = document.createElement('style');
  st.id = KF_ID;
  st.textContent =
    '@media (prefers-reduced-motion: no-preference){' +
    '@keyframes plick-paulina-nudge-in{from{transform:translateY(8px)}to{transform:translateY(0)}}' +
    '.plick-paulina-nudge-anim{animation:plick-paulina-nudge-in 220ms ease-out both}}';
  document.head.appendChild(st);
}

interface PaulinaNudgeBubbleProps {
  nudge: Nudge;
  onAccept: (n: Nudge) => void;
  onDismiss: (n: Nudge) => void;
}

export const PaulinaNudgeBubble: FC<PaulinaNudgeBubbleProps> = ({ nudge, onAccept, onDismiss }) => {
  const bubble: CSSProperties = {
    position: 'fixed',
    right: 24,
    bottom: 78, // justo encima de la pill (bottom-6 + alto de la pill).
    zIndex: Z.paulinaPill, // misma capa que la pill, por debajo del toast de pedido (Z.pedido=99).
    width: 280,
    background: C.primary,
    color: C.white,
    borderRadius: 12,
    padding: '13px 15px',
    boxShadow: '0 14px 36px rgba(37,55,70,.32)',
    opacity: 1,
  };

  return (
    <div role="dialog" aria-label={nudge.titulo} className="plick-paulina-nudge-anim" style={bubble}>
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          bottom: -5,
          right: 30,
          width: 12,
          height: 12,
          background: C.primary,
          transform: 'rotate(45deg)',
          borderRadius: 2,
        }}
      />
      <div className="mb-1 flex items-start justify-between gap-2">
        <p className="text-sm font-bold leading-snug">{nudge.titulo}</p>
        <button
          type="button"
          aria-label="Descartar"
          onClick={() => onDismiss(nudge)}
          style={{ background: 'transparent', border: 0, color: 'rgba(255,255,255,.7)', cursor: 'pointer', padding: 0 }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
      </div>
      <p className="mb-3 text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,.82)' }}>
        {nudge.mensaje}
      </p>
      <button
        type="button"
        onClick={() => onAccept(nudge)}
        className="w-full rounded-lg px-3 py-2 text-sm font-semibold"
        style={{ background: C.white, color: C.primary }}
      >
        {nudge.cta.label}
      </button>
    </div>
  );
};
